import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  CustomForm,
  CustomFormItem,
  CustomTextInput,
  CustomMaskInput,
  CustomInput,
  Text,
  useText,
} from '@src/components';
import {
  getVehicleDetailList,
  postUpdateVehicleDriver,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementDetailSlice';
import { Form, Row, Col, Button, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { EditOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { formMailRegex, formPhoneRegex } from '@src/utils/formRule';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import '@src/styles/addNewCar/addDriverSelectInformation.scss';

const VehicleDriverEditInformation = ({ vehicleDetailList, vehicleManagmentItem }) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const isMobil = useMediaQuery({ query: '(max-width: 1199px)' });
  const [isEditing, setIsEditing] = useState(false);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);
  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { loading: loadingUpdateVehicleDriver } = useSelector(
    (state) => state.vehicleManagementDetail,
  );
  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const driverInformation = useText('driverInformation');
  const edit2 = useText('edit2');
  const namSurnameText = useText('nameSurname');
  const emailText = useText('emailText');
  const phoneText = useText('phone');

  useEffect(() => {
    if (vehicleDetailList) {
      form.setFieldsValue({
        driverNameSurname: vehicleDetailList.driverNameSurname,
        driverGsm: vehicleDetailList.driverGsm,
        driverEmail: vehicleDetailList.driverEmail,
      });
    }
  }, [vehicleDetailList, form]);

  const formatPhoneNumber = (phone) => {
    let formattedPhone = phone.replace(/[^\d]/g, '');
    return formattedPhone.startsWith('90') ? formattedPhone.slice(2) : formattedPhone;
  };

  const handleEditClick = () => setIsEditing(true);

  const handleSave = () => {
    form
      .validateFields()
      .then(async (values) => {
        vehicleDetailList?.vehicleDeviceTypes?.map(async (item, index) => {
          const request = {
            vehicleDrivers: [
              {
                vehicleId: vehicleDetailList.vehicleId,
                vehicleDetailId: item.vehicleDetailId,
                driverNameSurname: values.driverNameSurname,
                driverGsm: values.driverGsm,
                driverEmail: values.driverEmail,
              },
            ],
          };
          const response = await dispatch(postUpdateVehicleDriver({ request })).unwrap();
          if (index === vehicleDetailList.vehicleDeviceTypes.length - 1) {
            if (response.isSuccess) {
              if (vehicleManagmentItem) {
                await dispatch(
                  getVehicleDetailList({
                    customerId:
                      isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
                    id: vehicleManagmentItem.vehicleId,
                    providerType: vehicleManagmentItem.providerType,
                  }),
                );
              }
              api.success({
                message: driverInformation,
                description: transactionSuccessfullyCompleted2,
                duration: 2,
              });
            }
            setIsEditing(false);
          }
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue({
      driverNameSurname: vehicleDetailList.driverNameSurname,
      driverGsm: formatPhoneNumber(vehicleDetailList.driverGsm || ''),
      driverEmail: vehicleDetailList.driverEmail,
    });
    setIsEditing(false);
  };

  const handleValuesChange = debounce((changedValues, allValues) => {
    console.log(':rocket: ~ handleValuesChange ~ allValues:', allValues);
  }, 300);

  const renderInputComponent = (inputType, name, placeholder) => {
    if (inputType === 'text' && name === 'driverNameSurname') {
      return (
        <CustomTextInput
          id={`${name}-textInput-edit`}
          data-testid={`drive-${name}`}
          placeholder={placeholder}
          autoComplete="off"
          height={36}
          onBlur={(e) => {
            const value = e.target.value;
            const formattedValue = value
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
            form.setFieldsValue({ [name]: formattedValue });
          }}
          disabled={!isEditing}
        />
      );
    } else if (inputType === 'mask') {
      return (
        <CustomMaskInput
          id={`${name}-maskInput-edit`}
          mask={'+\\90 (999) 999 99 99'}
          disabled={!isEditing}
        >
          <CustomInput
            id={`${name}-input-edit`}
            data-testid={`drive-mobile-phones`}
            placeholder={placeholder}
            disabled={!isEditing}
          />
        </CustomMaskInput>
      );
    } else {
      return (
        <CustomInput
          id={`${name}-input-edit`}
          data-testid={`drive-${name}`}
          placeholder={placeholder}
          disabled={!isEditing}
        />
      );
    }
  };

  const renderFormItem = (label, name, rules, placeholder, inputType) => (
    <Col span={24}>
      <CustomFormItem
        label={<Text t={label} />}
        name={name}
        className={'delivery-address-county-form-item'}
        rules={rules}
        validateTrigger="onBlur"
        style={{
          columnGap: name === 'driverNameSurname' ? '0.8em' : '1.5em',
          paddingBottom: isMobil ? '0px' : '10px',
        }}
      >
        {renderInputComponent(inputType, name, placeholder)}
      </CustomFormItem>
    </Col>
  );

  return (
    <div
      className="add-driver-select-information"
      style={{ backgroundColor: '#fff', padding: '0px 8px 0px', borderRadius: '10px' }}
    >
      {contextHolder}
      <Row
        justify={'space-between'}
        align="middle"
        className="header-row-title-3"
        style={{ backgroundColor: '#fff' }}
      >
        {!isEditing ? (
          <span style={{ color: '#00A483' }}>
            <Text t="driverInformation" />
          </span>
        ) : (
          ''
        )}
        {isEditing ? (
          <div style={{ display: 'flex', flex: 'auto', justifyContent: 'end', gap: '10px' }}>
            <Button style={{ backgroundColor: '#FF4D4F', color: '#fff' }} onClick={handleCancel}>
              <Text t="giveUp" />
            </Button>
            <Button
              style={{ backgroundColor: '#00A483', color: '#fff' }}
              loading={loadingUpdateVehicleDriver.postUpdateVehicleDriver}
              disabled={loadingUpdateVehicleDriver.postUpdateVehicleDriver}
              onClick={handleSave}
            >
              <Text t="cardParameterSave" />
            </Button>
          </div>
        ) : (
          <Button
            type="text"
            icon={<EditOutlined style={{ color: '#0F172A', fontSize: '24px' }} />}
            onClick={handleEditClick}
            style={{ display: 'flex', alignItems: 'end' }}
          >
            {!isMobil ? edit2 : ''}
          </Button>
        )}
      </Row>
      <CustomForm
        name={`driverForm-edit`}
        className="filter-form"
        autoComplete="off"
        initialValues={{}}
        layout={!isMobil ? 'horizontal' : 'vertical'}
        form={form}
        onValuesChange={handleValuesChange}
      >
        <Row
          gutter={{ xs: 16, sm: 16, md: 24, lg: 24 }}
          className="form-item"
          style={{ marginBottom: '10px' }}
        >
          {renderFormItem(namSurnameText, 'driverNameSurname', [], namSurnameText, 'text')}
          {renderFormItem(
            phoneText,
            'driverGsm',
            [
              {
                validator: formPhoneRegex,
                message: <Text t="enterDriverPhoneNumberCompletely" />,
              },
            ],
            '( _ _ _ ) _ _ _   _ _   _ _',
            'mask',
          )}
          {renderFormItem(
            emailText,
            'driverEmail',
            [{ validator: formMailRegex, message: <Text t="enterValidDriverEMail" /> }],
            emailText,
            'text',
          )}
        </Row>
      </CustomForm>
    </div>
  );
};

VehicleDriverEditInformation.propTypes = {
  vehicleDetailList: PropTypes.object,
  vehicleManagmentItem: PropTypes.object,
};

export default VehicleDriverEditInformation;
